<script setup lang="ts">
import type { CategoryType } from "mws-ts-v2-sdk";

type Props = {
  id: string;
  slug: string;
  name: string;
  type?: CategoryType;
  isFollowedByUser?: boolean;
  variant?: "default" | "text";
  source: "categoryPage" | "categoryListBlock";
};

const {
  variant = "default",
  isFollowedByUser = false,
  id,
  name,
  source,
} = defineProps<Props>();

const onFollowAuthCheck = useFollowAuthCheck();

const { onToggleFollow, followStatus } = useFollow({
  type: FollowFeatureType.Category,
});

const onFollow = () => {
  onFollowAuthCheck({
    followType: FollowFeatureType.Category,
    id,
    followSource: source,
    source: "FOLLOWING",
    name,
  });

  onToggleFollow({
    id,
    newFollowStatus: !isFollowedByUser,
    trackData: {
      followType: source,
    },
  });
};

const { t } = useI18n();
const platformStore = usePlatformStore();

const followText = computed(() =>
  !platformStore.isFrameTheGame
    ? t("customerPreferences.actions.follow")
    : t("customerPreferences.actions.favourite"),
);

const unfollowText = computed(() =>
  !platformStore.isFrameTheGame
    ? t("customerPreferences.actions.following")
    : t("customerPreferences.actions.favourite"),
);

const buttonText = computed(() =>
  isFollowedByUser ? unfollowText.value : followText.value,
);
</script>

<template>
  <span>
    <ClientOnly>
      <BaseButton
        class="category-follow__button"
        color-scheme="secondary"
        :class="`category-follow__button--variant-${variant}`"
        :disabled="followStatus === 'pending'"
        :data-cy="`follow-button-${id}${isFollowedByUser ? '-active' : ''}`"
        :aria-label="`${buttonText} ${name}`"
        @click="onFollow"
      >
        <BaseIcon
          class="category-follow__icon"
          :name="isFollowedByUser ? 'filledHeart' : 'heart'"
          :size="18"
        />
        <template v-if="variant === 'text'">
          {{ buttonText }}
        </template>
      </BaseButton>

      <template #fallback>
        <BaseSkeletonLoader
          class="category-follow__loader"
          :width="variant === 'default' ? 36 : 110"
          :height="variant === 'default' ? 36 : 48"
        />
      </template>
    </ClientOnly>
  </span>
</template>

<style lang="postcss" scoped>
.category-follow {
  &__button,
  &__loader {
    @apply w-full;
  }

  &__button {
    @apply flex items-center justify-center gap-2;

    &--variant-default {
      @apply w-[36px] h-[36px];
    }
  }
}
</style>
